import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { auth, db } from '../../config/firebase';
import { 
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser 
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { User } from '../../config/types';

interface AuthState {
  user: User | null;
  userInfo: any | null;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  userInfo: null,
  loading: true, // Start with loading true
  error: null
};

// Check authentication status
export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async () => {
    return new Promise<User | null>((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        unsubscribe(); // Unsubscribe immediately as we only need to check once
        
        if (firebaseUser) {
          try {
            // Get additional user data from Firestore
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
    if (userDoc.exists()) {
              const userData = userDoc.data();
              resolve({
                uid: firebaseUser.uid,
                email: firebaseUser.email || '',
                displayName: userData.name || '',
                accountType: userData.accountType || 'driver',
                dogtag: userData.dogtag || ''
              });
            } else {
              resolve(null);
            }
  } catch (error) {
            reject(error);
          }
        } else {
          resolve(null);
  }
      });
    });
  }
);

// Login
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      
      if (!userDoc.exists()) {
        throw new Error('User data not found');
      }

      const userData = userDoc.data();
      return {
        uid: userCredential.user.uid,
        email: userCredential.user.email || '',
        displayName: userData.name || '',
        accountType: userData.accountType || 'driver',
        dogtag: userData.dogtag || ''
      };
    } catch (error: any) {
      throw new Error(error.message || 'Failed to login');
    }
  }
);

// Logout
export const logout = createAsyncThunk(
  'auth/logout',
  async () => {
    await firebaseSignOut(auth);
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Check Auth
      .addCase(checkAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.userInfo = action.payload;
        state.error = null;
      })
      .addCase(checkAuth.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.userInfo = null;
        state.error = action.error.message || 'Failed to check authentication status';
      })
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.userInfo = action.payload;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to login';
      })
      // Logout
      .addCase(logout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.userInfo = null;
        state.error = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to logout';
      });
  }
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer; 